import { useCallback, useMemo, useRef, useState } from "react";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import { useGUIEditorContext } from "../../../../../../hook/context/gui";
import type { BlockPlotModelT } from "../../../../../../schema/block/plot";

const formatZoomValue = (value: string) => {
  const v = value.replace(",", ".");
  if (!Number.isNaN(parseFloat(v))) return parseFloat(v);
  if (value === "") return null;
  return value;
};

const useLayerZoomUpdate = (
  block: BlockPlotModelT | undefined,
  position: number,
  axis: "x" | "y",
  limit: "min" | "max",
) => {
  const editor = useDashboardEditorContext();

  return useCallback(
    (value: string) => {
      if (!block) return;
      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: block.config.layers.map((layer, i) => ({
            ...layer,
            zoom:
              i === position
                ? {
                    ...layer.zoom,
                    [axis]: {
                      ...(layer.zoom?.[axis] ?? { min: null, max: null }),
                      [limit]: formatZoomValue(value),
                    },
                  }
                : layer.zoom,
          })),
        },
      });
    },
    [axis, block, editor, limit, position],
  );
};

export const useLayerUpdateZoomMin = (position: number, axis: "x" | "y") => {
  const { block } = useGUIEditorContext();
  const [zoomMin, setZoomMin] = useState(block?.config.layers[position]?.zoom?.[axis]?.min);

  const layerUpdateZoom = useLayerZoomUpdate(block, position, axis, "min");

  const layerUpdateZoomMin = useCallback(
    (value: string) => {
      if (!block) return;
      setZoomMin(value);
      layerUpdateZoom(value);
    },
    [block, layerUpdateZoom],
  );

  return useMemo(
    () => ({
      zoomMin,
      layerUpdateZoomMin,
    }),
    [zoomMin, layerUpdateZoomMin],
  );
};

export const useLayerUpdateZoomMax = (position: number, axis: "x" | "y") => {
  const { block } = useGUIEditorContext();
  const [zoomMax, setZoomMax] = useState(block?.config.layers[position]?.zoom?.[axis]?.max);

  const layerUpdateZoom = useLayerZoomUpdate(block, position, axis, "max");

  const layerUpdateZoomMax = useCallback(
    (value: string) => {
      if (!block) return;
      setZoomMax(value);
      layerUpdateZoom(value);
    },
    [block, layerUpdateZoom],
  );

  return useMemo(
    () => ({
      zoomMax,
      layerUpdateZoomMax,
    }),
    [zoomMax, layerUpdateZoomMax],
  );
};

export const useLayerClearZoomCallback = (position: number, axis: "x" | "y") => {
  const editor = useDashboardEditorContext();
  const { block } = useGUIEditorContext();

  const [withZoomOptions, setWithZoomOptions] = useState(
    () =>
      block?.config.layers[position]?.zoom?.[axis]?.min !== null ||
      block?.config.layers[position]?.zoom?.[axis]?.max !== null,
  );

  const previousZoomValues = useRef<
    (
      | {
          x?: { min: string | number | null; max: string | number | null };
          y?: { min: string | number | null; max: string | number | null };
        }
      | undefined
    )[]
  >([]);

  const editorLayerClearZoomCallback = useDebouncedCallback(
    () => {
      if (!block) return;
      previousZoomValues.current = block.config.layers.map(layer => layer.zoom);

      void editor?.updateBlock({
        ...block,
        config: {
          ...block.config,
          layers: block.config.layers.map((layer, i) => ({
            ...layer,
            zoom:
              i === position
                ? {
                    ...layer.zoom,
                    [axis]: {
                      min: null,
                      max: null,
                    },
                  }
                : layer.zoom,
          })),
        },
      });
    },
    [axis, block, editor, position],
    500,
  );

  const editorLayerRevertZoomCallback = useCallback(() => {
    if (!block) return;
    void editor?.updateBlock({
      ...block,
      config: {
        ...block.config,
        layers: block.config.layers.map((layer, i) => ({
          ...layer,
          zoom: previousZoomValues.current[i],
        })),
      },
    });
  }, [block, editor]);

  const toggleZoomOptions = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setWithZoomOptions(e.target.checked);
      if (e.target.checked) {
        editorLayerRevertZoomCallback();
      } else {
        editorLayerClearZoomCallback();
      }
    },
    [editorLayerClearZoomCallback, editorLayerRevertZoomCallback],
  );

  return useMemo(
    () => ({ withZoomOptions, toggleZoomOptions }),
    [withZoomOptions, toggleZoomOptions],
  );
};
